import React, { useState, useEffect } from 'react';
import { useConsent, CONSENT_TYPES } from '../contexts/ConsentContext';

function ConsentBanner() {
  const { 
    consent, 
    showBanner, 
    updateConsent, 
    acceptAll, 
    rejectAll,
    setShowBanner 
  } = useConsent();
  
  const [showDetails, setShowDetails] = useState(false);
  const [tempConsent, setTempConsent] = useState(consent);

  // Synchronize tempConsent with consent when it changes (e.g., loaded from cookies)
  useEffect(() => {
    setTempConsent(consent);
  }, [consent]);

  if (!showBanner) return null;

  const handleToggleConsent = (type) => {
    if (type === CONSENT_TYPES.NECESSARY) return; // Cannot disable necessary cookies
    
    setTempConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSavePreferences = () => {
    updateConsent(tempConsent);
    setShowDetails(false);
  };

  const handleAcceptAll = () => {
    acceptAll();
    setShowDetails(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setShowDetails(false);
  };

  const consentDescriptions = {
    [CONSENT_TYPES.NECESSARY]: {
      title: 'Niezbędne',
      description: 'Te pliki cookie są konieczne do prawidłowego funkcjonowania strony internetowej i nie można ich wyłączyć.'
    },
    [CONSENT_TYPES.ANALYTICS]: {
      title: 'Analityczne',
      description: 'Pomagają nam zrozumieć, jak odwiedzający korzystają z naszej strony, dzięki czemu możemy ją ulepszać.'
    },
    [CONSENT_TYPES.MARKETING]: {
      title: 'Marketingowe',
      description: 'Używane do wyświetlania spersonalizowanych reklam i śledzenia skuteczności kampanii reklamowych.'
    },
    [CONSENT_TYPES.PREFERENCES]: {
      title: 'Preferencje',
      description: 'Pozwalają zapamiętać Twoje preferencje i ustawienia, aby poprawić komfort korzystania ze strony.'
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {!showDetails ? (
          // Simple banner view
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Szanujemy Twoją prywatność
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Używamy plików cookie i podobnych technologii, aby zapewnić najlepsze doświadczenia na naszej stronie internetowej. 
                  Niektóre z nich są niezbędne do funkcjonowania strony, podczas gdy inne pomagają nam ją ulepszać i personalizować treści.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Zaakceptuj wszystkie
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Odrzuć wszystkie
                  </button>
                  <button
                    onClick={() => {
                      setTempConsent(consent); // Ensure we have the latest consent values
                      setShowDetails(true);
                    }}
                    className="text-blue-600 hover:text-blue-700 px-6 py-2 text-sm font-medium transition-colors underline"
                  >
                    Dostosuj ustawienia
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowBanner(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Zamknij"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          // Detailed preferences view
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Ustawienia prywatności
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Wróć"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Możesz dostosować swoje preferencje dotyczące plików cookie. Kliknij na każdą kategorię, aby dowiedzieć się więcej i zmienić nasze domyślne ustawienia.
            </p>

            <div className="space-y-4 mb-6">
              {Object.entries(CONSENT_TYPES).map(([key, type]) => (
                <div key={type} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">
                        {consentDescriptions[type].title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {consentDescriptions[type].description}
                      </p>
                    </div>
                    <div className="ml-4">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tempConsent[type]}
                          onChange={() => handleToggleConsent(type)}
                          disabled={type === CONSENT_TYPES.NECESSARY}
                          className="sr-only peer"
                        />
                        <div className={`relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                          type === CONSENT_TYPES.NECESSARY ? 'opacity-50 cursor-not-allowed' : ''
                        }`}></div>
                      </label>
                    </div>
                  </div>
                  {type === CONSENT_TYPES.NECESSARY && (
                    <p className="text-xs text-gray-500 mt-2">
                      Te pliki cookie są zawsze aktywne
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleSavePreferences}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Zapisz preferencje
              </button>
              <button
                onClick={handleAcceptAll}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Zaakceptuj wszystkie
              </button>
              <button
                onClick={handleRejectAll}
                className="text-gray-600 hover:text-gray-800 px-6 py-2 text-sm font-medium transition-colors"
              >
                Odrzuć wszystkie
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Więcej informacji o naszej polityce prywatności znajdziesz w{' '}
                <a href="/polityka-prywatnosci" className="text-blue-600 hover:text-blue-700 underline">
                  Polityce Prywatności
                </a>
                .
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsentBanner;
